/**
 * Emissions Tracker RTK Query API
 *
 * API endpoints for emission tracking following the g17ecoApi pattern.
 */

import { g17ecoApi, transformResponse } from '@api/g17ecoApi';
import type { AxiosQueryMeta } from '@api/axiosQuery';
import type { ApiResponse, PaginatedApiResponse } from '../../../types/api';
import type {
  ActivityRecord,
  ActivityListResponse,
  ActivityWithCalculation,
  CreateActivityRequest,
  UpdateActivityRequest,
  EmissionCalculation,
  EmissionSummary,
  DataQualitySummary,
  ActivityTypeReport,
  MonthlyReport,
  Scope2DualResult,
  Scope2DualSummary,
  MarketBasedInput,
  EmissionScope,
  Scope1ActivityType,
  Scope2Method,
  IntensityMetrics,
  BusinessMetrics,
  BusinessMetricsRequest,
  EmissionsTarget,
  CreateTargetRequest,
  UpdateTargetRequest,
  EmissionFactor,
  EmissionFactorSourceDefinition,
  EmissionFactorsListResponse,
  EmissionFactorsQueryParams,
  Pagination,
  RegionOption,
  FacilitiesComparisonResponse,
  FacilitiesComparisonParams,
  SourcePeriodReport,
  SourcePeriodParams,
  EmissionSettings,
  UpdateAllSettingsRequest,
  EmissionFactorImportResult,
  EmissionsPPTXReportItem,
  ActivityOptions,
  BulkImport,
  AlternativeFactor,
  AssetType,
  EmissionAsset,
  CreateAssetRequest,
  UpdateAssetRequest,
  AssetListResponse,
  AssetDeleteMode,
  AssetSummary,
  AssetComparisonResponse,
  IntensityTreemapResponse,
  EntityMonthlyResponse,
  OnboardingStatusResponse,
  OnboardingStatus,
  EmissionEvidenceListItem,
  EvidenceActivityLink,
  EvidenceType,
  UploadSession,
  SessionFile,
  SessionFileMetadataUpdate,
  UploadSessionStatus,
  PromoteFileResult,
  UploadEvidenceResult,
} from '../types/emissions';
import type { ExtractInvoiceResult } from '../types/invoiceExtraction';

const ACTIVITIES_TAG = 'emission-activities';
const CALCULATIONS_TAG = 'emission-calculations';
const GENERATED_REPORTS_TAG = 'generated-reports';
const REPORTS_TAG = 'emission-reports';
const METRICS_TAG = 'emission-metrics';
const TARGETS_TAG = 'emission-targets';
const FACTORS_TAG = 'emission-factors';
const FACILITIES_COMPARISON_TAG = 'facilities-comparison';
const SETTINGS_TAG = 'emission-settings';
const IMPORTS_TAG = 'emission-imports';
const ASSETS_TAG = 'emission-assets';
const ONBOARDING_TAG = 'emission-onboarding';
const EVIDENCE_TAG = 'emission-evidence';
const SESSION_TAG = 'emission-upload-session';

// Transform for paginated responses - extracts data and pagination from flat response
const transformPaginatedResponse = <T>(response: PaginatedApiResponse<T>): ActivityListResponse => ({
  data: response.data as unknown as ActivityRecord[],
  pagination: response.pagination,
});

interface ActivityListParams {
  initiativeId: string;
  scope?: EmissionScope;
  activityType?: string;
  assetId?: string;
  /** Comma-separated BulkImport ids — when present, restricts results to activities created by these imports. */
  bulkImportIds?: string;
  calculationStatus?: string;
  search?: string;
  periodStart?: string;
  periodEnd?: string;
  limit?: number;
  offset?: number;
  includeChildren?: boolean;
  sort?: string;
  sortDir?: 'asc' | 'desc';
}

interface CalculationListParams {
  initiativeId: string;
  activityId?: string;
  scope?: EmissionScope;
  scope2Method?: Scope2Method;
  limit?: number;
  offset?: number;
}

interface ReportParams {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  scopes?: EmissionScope[];
  includeChildren?: boolean;
  scope2Method?: Scope2Method;
}

interface DataQualityParams {
  initiativeId: string;
  periodStart?: string;
  periodEnd?: string;
  includeChildren?: boolean;
}

interface SummaryParams {
  initiativeId: string;
  periodStart?: string;
  periodEnd?: string;
  includeChildren?: boolean;
  scope2Method?: Scope2Method;
}

interface CalculationOptions {
  gwpMethodology?: 'AR4' | 'AR5' | 'AR6';
  scope2Method?: Scope2Method;
  recalculate?: boolean;
}

/**
 * Body shape for the draft factor-search endpoint
 * (`POST /:initiativeId/emissions/activities/factor-search`). Mirrors the
 * server zod schema. Scalars on the client; the server wraps `scope`,
 * `region`, and `unit` into single-element arrays before dispatching.
 */
export interface FactorSearchQuery {
  scope: EmissionScope;
  activityType: string;
  /** Optional in broad-search mode (clear to "any"); required otherwise. */
  region?: string;
  unit?: string;
  fuelType?: string;
  vehicleType?: string;
  refrigerantType?: string;
  gasType?: string;
  processType?: string;
  year?: number;
  /** Mirrors the activity row's `regionFallback` flag. (Preview mode only.) */
  regionFallback?: boolean;
  /** Mirrors the activity row's `yearFallback` flag. (Preview mode only.) */
  yearFallback?: boolean;
  /**
   * Free-text search query. Setting any of `query`, `source`, `page`,
   * `resultsPerPage` switches the endpoint into broad-search mode (no
   * fallback chain; pagination metadata returned).
   */
  query?: string;
  /** Source filter (e.g. 'DEFRA', 'EPA'). */
  source?: string;
  /** 1-indexed page. */
  page?: number;
  /** Page size; Climatiq cap is 100. */
  resultsPerPage?: number;
  /**
   * Climatiq unit_type filter (e.g. ['Energy'], ['Weight']). Set by the
   * caller from the activity's resolved unit type so the search is
   * constrained to compatible factors.
   */
  unitType?: string[];
}

export interface FactorSearchPagination {
  page: number;
  lastPage: number;
  total: number;
  resultsPerPage: number;
}

export interface FactorSearchPaginatedResult {
  alternatives: AlternativeFactor[];
  pagination: FactorSearchPagination;
}

export const emissionsApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: [ACTIVITIES_TAG, CALCULATIONS_TAG, GENERATED_REPORTS_TAG, REPORTS_TAG, METRICS_TAG, TARGETS_TAG, FACTORS_TAG, FACILITIES_COMPARISON_TAG, SETTINGS_TAG, IMPORTS_TAG, ASSETS_TAG, ONBOARDING_TAG, EVIDENCE_TAG, SESSION_TAG],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      // Activities CRUD
      getActivityYears: builder.query<number[], string>({
        transformResponse: (response: ApiResponse<{ years: number[] }>) => response.data.years,
        query: (initiativeId) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/years`,
          method: 'get',
        }),
        providesTags: [ACTIVITIES_TAG],
      }),

      getActivities: builder.query<ActivityListResponse, ActivityListParams>({
        transformResponse: transformPaginatedResponse,
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities`,
          method: 'get',
          params,
        }),
        providesTags: [ACTIVITIES_TAG],
      }),

      getActivity: builder.query<ActivityRecord, { initiativeId: string; activityId: string }>({
        transformResponse,
        query: ({ initiativeId, activityId }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/${activityId}`,
          method: 'get',
        }),
        providesTags: [ACTIVITIES_TAG],
      }),

      createActivity: builder.mutation<
        ActivityWithCalculation,
        { initiativeId: string; data: CreateActivityRequest }
      >({
        transformResponse,
        // Why: also invalidates REPORTS_TAG + ASSETS_TAG so any open
        // dashboards (asset trend chart, scope/source donuts, data-quality
        // grid, sources breakdown) refresh after the new activity lands.
        // Without these, the cell stays a gap until the user changes period.
        invalidatesTags: [ACTIVITIES_TAG, CALCULATIONS_TAG, REPORTS_TAG, ASSETS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, data }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities`,
          method: 'post',
          data,
        }),
      }),

      updateActivity: builder.mutation<
        ActivityWithCalculation,
        { initiativeId: string; activityId: string; data: UpdateActivityRequest }
      >({
        // Response is already in ActivityWithCalculation format: { data, calculation, notices }
        invalidatesTags: [ACTIVITIES_TAG, CALCULATIONS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, activityId, data }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/${activityId}`,
          method: 'put',
          data,
        }),
      }),

      deleteActivity: builder.mutation<void, { initiativeId: string; activityId: string }>({
        invalidatesTags: [ACTIVITIES_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, activityId }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/${activityId}`,
          method: 'delete',
        }),
      }),

      bulkDeleteActivities: builder.mutation<
        { deleted: number },
        { initiativeId: string; activityIds: string[]; includeChildren?: boolean }
      >({
        invalidatesTags: [ACTIVITIES_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, activityIds, includeChildren }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/bulk`,
          method: 'delete',
          data: { activityIds, includeChildren },
        }),
      }),

      bulkCreateActivities: builder.mutation<
        {
          summary: { successful: number; failed: number; total: number };
          results: Array<{
            index: number;
            success: boolean;
            data?: ActivityWithCalculation;
            error?: string;
          }>;
        },
        { initiativeId: string; activities: CreateActivityRequest[] }
      >({
        transformResponse,
        invalidatesTags: [ACTIVITIES_TAG, CALCULATIONS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, activities }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/bulk`,
          method: 'post',
          data: { activities },
        }),
      }),

      // Calculations
      getCalculations: builder.query<
        { data: EmissionCalculation[]; pagination: { total: number; limit: number; offset: number } },
        CalculationListParams
      >({
        transformResponse,
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/calculations`,
          method: 'get',
          params,
        }),
        providesTags: [CALCULATIONS_TAG],
      }),

      getCalculation: builder.query<
        EmissionCalculation,
        { initiativeId: string; calculationId: string }
      >({
        transformResponse,
        query: ({ initiativeId, calculationId }) => ({
          url: `/initiatives/${initiativeId}/emissions/calculations/${calculationId}`,
          method: 'get',
        }),
        providesTags: [CALCULATIONS_TAG],
      }),

      triggerCalculation: builder.mutation<
        { calculation: EmissionCalculation; notices?: string[] },
        { initiativeId: string; activityId: string; options?: CalculationOptions }
      >({
        transformResponse,
        invalidatesTags: [ACTIVITIES_TAG, CALCULATIONS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, activityId, options }) => ({
          url: `/initiatives/${initiativeId}/emissions/calculations/calculate`,
          method: 'post',
          data: { activityId, options },
        }),
      }),

      calculateScope2Dual: builder.mutation<
        { data: Scope2DualResult; notices?: string[] },
        {
          initiativeId: string;
          activityId: string;
          marketBasedInput?: MarketBasedInput;
          options?: CalculationOptions;
        }
      >({
        transformResponse,
        invalidatesTags: [ACTIVITIES_TAG, CALCULATIONS_TAG, REPORTS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, activityId, marketBasedInput, options }) => ({
          url: `/initiatives/${initiativeId}/emissions/calculations/scope2-dual`,
          method: 'post',
          data: { activityId, marketBasedInput, options },
        }),
      }),

      recalculateAll: builder.mutation<
        { summary: { successful: number; failed: number; total: number }; message: string },
        { initiativeId: string; options?: CalculationOptions }
      >({
        transformResponse,
        invalidatesTags: [ACTIVITIES_TAG, CALCULATIONS_TAG, REPORTS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, options }) => ({
          url: `/initiatives/${initiativeId}/emissions/calculations/recalculate`,
          method: 'post',
          data: { options },
        }),
      }),

      getCalculationHistory: builder.query<
        EmissionCalculation[],
        { initiativeId: string; activityId: string }
      >({
        transformResponse,
        query: ({ initiativeId, activityId }) => ({
          url: `/initiatives/${initiativeId}/emissions/calculations/activity/${activityId}/history`,
          method: 'get',
        }),
        providesTags: [CALCULATIONS_TAG],
      }),

      getFactorAlternatives: builder.query<
        AlternativeFactor[],
        { initiativeId: string; activityId: string }
      >({
        transformResponse: (response: ApiResponse<{ alternatives: AlternativeFactor[] }>) =>
          response.data.alternatives,
        query: ({ initiativeId, activityId }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/${activityId}/factor-alternatives`,
          method: 'get',
        }),
        providesTags: [ACTIVITIES_TAG],
      }),

      findFactorAlternatives: builder.mutation<
        AlternativeFactor[],
        { initiativeId: string; activityId: string }
      >({
        transformResponse: (response: ApiResponse<{ alternatives: AlternativeFactor[] }>) =>
          response.data.alternatives,
        invalidatesTags: [ACTIVITIES_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, activityId }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/${activityId}/find-alternatives`,
          method: 'post',
        }),
      }),

      /**
       * Search Climatiq for emission factors that match a *draft* row's
       * dimensions — used by the multi-add Entry step to surface the
       * resolved factor before the activity exists in the DB. Returns the
       * same `AlternativeFactor[]` shape as `findFactorAlternatives`. Cache-
       * read: this hits the `ClimatiqSearchCache` populated by the route.
       */
      factorSearch: builder.mutation<
        AlternativeFactor[],
        { initiativeId: string; query: FactorSearchQuery }
      >({
        transformResponse: (response: ApiResponse<{ alternatives: AlternativeFactor[] }>) =>
          response.data.alternatives,
        query: ({ initiativeId, query }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/factor-search`,
          method: 'post',
          data: query,
        }),
      }),

      /**
       * Broad-search variant of `factorSearch` for the FactorSearchModal.
       * Returns pagination metadata. The same endpoint serves both modes;
       * the broad-search branch is triggered server-side when the body
       * carries `query`, `source`, `page`, or `resultsPerPage`.
       */
      factorSearchPaginated: builder.mutation<
        FactorSearchPaginatedResult,
        { initiativeId: string; query: FactorSearchQuery }
      >({
        transformResponse: (response: ApiResponse<FactorSearchPaginatedResult>) => response.data,
        query: ({ initiativeId, query }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/factor-search`,
          method: 'post',
          data: query,
        }),
      }),

      overrideCalculationFactor: builder.mutation<
        { calculation: EmissionCalculation; notices?: string[] },
        { initiativeId: string; activityId: string; climatiqFactorId: string }
      >({
        transformResponse,
        invalidatesTags: [ACTIVITIES_TAG, CALCULATIONS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, activityId, climatiqFactorId }) => ({
          url: `/initiatives/${initiativeId}/emissions/calculations/calculate`,
          method: 'post',
          data: {
            activityId,
            options: { overrideClimatiqFactorId: climatiqFactorId, recalculate: true },
          },
        }),
      }),

      revertCalculationVersion: builder.mutation<
        { calculation: EmissionCalculation },
        { initiativeId: string; activityId: string; targetVersion: number }
      >({
        transformResponse,
        invalidatesTags: [ACTIVITIES_TAG, CALCULATIONS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, activityId, targetVersion }) => ({
          url: `/initiatives/${initiativeId}/emissions/calculations/revert`,
          method: 'post',
          data: { activityId, targetVersion },
        }),
      }),

      // Data Quality Summary (MongoDB aggregate — no row-limit issues)
      getDataQualitySummary: builder.query<DataQualitySummary, DataQualityParams>({
        transformResponse,
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/reports/data-quality`,
          method: 'get',
          params,
        }),
        providesTags: [ACTIVITIES_TAG],
      }),

      // Reports
      getEmissionsSummary: builder.query<EmissionSummary, SummaryParams>({
        transformResponse,
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/reports/summary`,
          method: 'get',
          params,
        }),
        providesTags: [REPORTS_TAG],
      }),

      getEmissionsByActivityType: builder.query<ActivityTypeReport[], ReportParams>({
        transformResponse: (response: ApiResponse<{ results: ActivityTypeReport[] }>) =>
          response.data?.results ?? [],
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/reports/by-activity-type`,
          method: 'get',
          params: {
            ...params,
            scopes: params.scopes?.join(','),
          },
        }),
        providesTags: [REPORTS_TAG],
      }),

      getEmissionsByMonth: builder.query<
        { results: MonthlyReport[]; unit: string; period: { start?: string; end?: string } },
        ReportParams
      >({
        transformResponse,
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/reports/by-month`,
          method: 'get',
          params: {
            ...params,
            scopes: params.scopes?.join(','),
          },
        }),
        providesTags: [REPORTS_TAG],
      }),

      getEmissionsBySourcePeriod: builder.query<SourcePeriodReport, SourcePeriodParams>({
        transformResponse,
        query: ({ initiativeId, scopes, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/reports/by-source-period`,
          method: 'get',
          params: {
            ...params,
            ...(scopes && scopes.length > 0 ? { scopes: scopes.join(',') } : {}),
          },
        }),
        providesTags: [REPORTS_TAG],
      }),

      getScope2DualSummary: builder.query<Scope2DualSummary, SummaryParams>({
        transformResponse,
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/reports/scope2-dual`,
          method: 'get',
          params,
        }),
        providesTags: [REPORTS_TAG],
      }),

      exportEmissions: builder.query<Blob, ReportParams>({
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/reports/export`,
          method: 'get',
          params: {
            ...params,
            scopes: params.scopes?.join(','),
          },
          responseType: 'blob',
        }),
      }),

      // Organization Metrics
      getIntensityMetrics: builder.query<IntensityMetrics, SummaryParams>({
        transformResponse,
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/metrics/intensity`,
          method: 'get',
          params,
        }),
        providesTags: [METRICS_TAG],
      }),

      getBusinessMetrics: builder.query<BusinessMetrics | null, SummaryParams>({
        transformResponse,
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/metrics/business`,
          method: 'get',
          params,
        }),
        providesTags: [METRICS_TAG],
      }),

      getBusinessMetricsList: builder.query<BusinessMetrics[], { initiativeId: string }>({
        transformResponse,
        query: ({ initiativeId }) => ({
          url: `/initiatives/${initiativeId}/emissions/metrics/business/list`,
          method: 'get',
        }),
        providesTags: [METRICS_TAG],
      }),

      updateBusinessMetrics: builder.mutation<
        BusinessMetrics,
        { initiativeId: string; data: BusinessMetricsRequest }
      >({
        transformResponse,
        invalidatesTags: [METRICS_TAG],
        query: ({ initiativeId, data }) => ({
          url: `/initiatives/${initiativeId}/emissions/metrics/business`,
          method: 'put',
          data,
        }),
      }),

      deleteBusinessMetrics: builder.mutation<void, { initiativeId: string; metricsId: string }>({
        invalidatesTags: [METRICS_TAG],
        query: ({ initiativeId, metricsId }) => ({
          url: `/initiatives/${initiativeId}/emissions/metrics/business/${metricsId}`,
          method: 'delete',
        }),
      }),

      // Targets
      getTargets: builder.query<
        { data: EmissionsTarget[]; pagination: { total: number; limit: number; offset: number } },
        { initiativeId: string; status?: string; limit?: number; offset?: number }
      >({
        transformResponse,
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/targets`,
          method: 'get',
          params,
        }),
        providesTags: [TARGETS_TAG],
      }),

      getTarget: builder.query<EmissionsTarget, { initiativeId: string; targetId: string }>({
        transformResponse,
        query: ({ initiativeId, targetId }) => ({
          url: `/initiatives/${initiativeId}/emissions/targets/${targetId}`,
          method: 'get',
        }),
        providesTags: [TARGETS_TAG],
      }),

      createTarget: builder.mutation<EmissionsTarget, { initiativeId: string; data: CreateTargetRequest }>({
        transformResponse,
        invalidatesTags: [TARGETS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, data }) => ({
          url: `/initiatives/${initiativeId}/emissions/targets`,
          method: 'post',
          data,
        }),
      }),

      updateTarget: builder.mutation<
        EmissionsTarget,
        { initiativeId: string; targetId: string; data: UpdateTargetRequest }
      >({
        transformResponse,
        invalidatesTags: [TARGETS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, targetId, data }) => ({
          url: `/initiatives/${initiativeId}/emissions/targets/${targetId}`,
          method: 'put',
          data,
        }),
      }),

      deleteTarget: builder.mutation<void, { initiativeId: string; targetId: string }>({
        invalidatesTags: [TARGETS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, targetId }) => ({
          url: `/initiatives/${initiativeId}/emissions/targets/${targetId}`,
          method: 'delete',
        }),
      }),

      // Staff-only: Seed sample activity data
      seedSampleData: builder.mutation<
        {
          message: string;
          parent: { successful: number; failed: number; calculated: number; total: number };
          children?: Array<{
            initiativeId: string;
            summary: { successful: number; failed: number; calculated: number; total: number };
          }>;
        },
        { initiativeId: string; includeChildren?: boolean }
      >({
        transformResponse,
        invalidatesTags: [ACTIVITIES_TAG, CALCULATIONS_TAG, REPORTS_TAG, FACILITIES_COMPARISON_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, includeChildren }) => ({
          url: `/initiatives/${initiativeId}/emissions/seed/sample`,
          method: 'post',
          data: { includeChildren },
        }),
      }),

      // Emission Factors (returns global + custom factors for initiative, with sources)
      getEmissionFactors: builder.query<
        EmissionFactorsListResponse,
        { initiativeId: string } & EmissionFactorsQueryParams
      >({
        transformResponse: (response: {
          data: {
            sources: EmissionFactorSourceDefinition[];
            regions: RegionOption[];
            years: number[];
            categories: string[];
            emissionFactors: EmissionFactor[];
          };
          pagination: Pagination;
        }): EmissionFactorsListResponse => ({
          sources: response.data.sources,
          regions: response.data.regions,
          years: response.data.years,
          categories: response.data.categories,
          emissionFactors: response.data.emissionFactors,
          pagination: response.pagination,
        }),
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/factors`,
          method: 'get',
          params,
        }),
        providesTags: [FACTORS_TAG],
      }),

      // Single Emission Factor by ID
      getEmissionFactor: builder.query<EmissionFactor, { initiativeId: string; factorId: string }>({
        transformResponse,
        query: ({ initiativeId, factorId }) => ({
          url: `/initiatives/${initiativeId}/emissions/factors/${factorId}`,
          method: 'get',
        }),
        providesTags: [FACTORS_TAG],
      }),

      // Emission Factor Import - Download template Excel file
      getFactorImportTemplate: builder.query<Blob, { initiativeId: string }>({
        query: ({ initiativeId }) => ({
          url: `/initiatives/${initiativeId}/emissions/factors/import/template`,
          method: 'get',
          responseType: 'blob',
        }),
      }),

      // Emission Factor Import - Upload and import Excel file
      importFactorsFromExcel: builder.mutation<
        EmissionFactorImportResult,
        { initiativeId: string; file: File }
      >({
        invalidatesTags: [FACTORS_TAG],
        transformResponse,
        query: ({ initiativeId, file }) => {
          const formData = new FormData();
          formData.append('file', file);
          return {
            url: `/initiatives/${initiativeId}/emissions/factors/import`,
            method: 'post',
            data: formData,
          };
        },
      }),

      // Delete a custom emission factor
      deleteEmissionFactor: builder.mutation<void, { initiativeId: string; factorId: string }>({
        invalidatesTags: [FACTORS_TAG],
        query: ({ initiativeId, factorId }) => ({
          url: `/initiatives/${initiativeId}/emissions/factors/${factorId}`,
          method: 'delete',
        }),
      }),

      // Activity Options - returns all valid enum values for activity fields
      getActivityOptions: builder.query<ActivityOptions, { initiativeId: string }>({
        transformResponse: (response: ApiResponse<ActivityOptions>) => response.data,
        query: ({ initiativeId }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/options`,
          method: 'get',
        }),
      }),

      // Activity Import Template - Download XLSX template with dropdowns for all enum fields
      getActivityImportTemplate: builder.query<Blob, { initiativeId: string; scope: EmissionScope; activityType?: Scope1ActivityType }>({
        query: ({ initiativeId, scope, activityType }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/import/template`,
          method: 'get',
          params: { scope, ...(activityType && { activityType }) },
          responseType: 'blob',
        }),
      }),

      // Activity Seed Template - Download XLSX template pre-filled with seed data (staff only)
      getActivitySeedTemplate: builder.query<Blob, { initiativeId: string; scope: EmissionScope; activityType?: Scope1ActivityType; rowCount: number; startYear: number; endYear: number }>({
        query: ({ initiativeId, scope, activityType, rowCount, startYear, endYear }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/import/seed-template`,
          method: 'get',
          params: { scope, ...(activityType && { activityType }), rowCount, startYear, endYear },
          responseType: 'blob',
        }),
      }),

      // Asset Import Template - Download XLSX template for bulk asset import
      getAssetImportTemplate: builder.query<Blob, { initiativeId: string; assetType: string }>({
        query: ({ initiativeId, assetType }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/import/asset-template`,
          method: 'get',
          params: { assetType },
          responseType: 'blob',
        }),
      }),

      // Asset Seed Template - Download XLSX template pre-filled with asset seed data (staff only)
      getAssetSeedTemplate: builder.query<Blob, { initiativeId: string; assetType: string; rowCount: number; startYear: number; endYear: number }>({
        query: ({ initiativeId, assetType, rowCount, startYear, endYear }) => ({
          url: `/initiatives/${initiativeId}/emissions/activities/import/asset-seed-template`,
          method: 'get',
          params: { assetType, rowCount, startYear, endYear },
          responseType: 'blob',
        }),
      }),

      // Asset Import — single endpoint for both manual and bulk-XLSX
      // asset creation. File optional: supplied for XLSX uploads (gets
      // attached as evidence on every activity in the group), omitted
      // for manually-added assets. Either way the server creates a
      // BulkImport record so the group is rollback-able via undoImport.
      createBulkAssetImport: builder.mutation<
        BulkImport,
        { initiativeId: string; file?: File; assets: string; assetType: string }
      >({
        transformResponse,
        invalidatesTags: [ACTIVITIES_TAG, CALCULATIONS_TAG, IMPORTS_TAG, ASSETS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, file, assets, assetType }) => {
          const formData = new FormData();
          if (file) {
            formData.append('file', file);
          }
          formData.append('assets', assets);
          formData.append('assetType', assetType);
          return {
            url: `/initiatives/${initiativeId}/emissions/assets/bulk-import`,
            method: 'post',
            data: formData,
          };
        },
      }),

      // Activity Export - Export activities to Excel (single or multiple)
      // Initiative name for filename comes from server via Content-Disposition header
      exportActivities: builder.mutation<
        { blob: Blob; filename: string },
        { initiativeId: string; activityIds: string[] }
      >({
        queryFn: async ({ initiativeId, activityIds }, _api, _extraOptions, baseQuery) => {
          const result = await baseQuery({
            url: `/initiatives/${initiativeId}/emissions/activities/export`,
            method: 'post',
            data: { activityIds },
            responseType: 'blob',
          });

          if (result.error) {
            return { error: result.error };
          }

          // Extract filename from Content-Disposition header or use default
          const blob = result.data as Blob;
          const meta = result.meta as AxiosQueryMeta | undefined;
          const contentDisposition = meta?.headers?.['content-disposition'] ?? '';
          const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
          const filename = filenameMatch?.[1] ?? `activities-export-${new Date().toISOString().slice(0, 10)}.xlsx`;

          return { data: { blob, filename } };
        },
      }),

      // Activity CSV Export - Export filtered activities to CSV (max 1,000 rows)
      exportActivitiesCSV: builder.mutation<
        { blob: Blob; filename: string },
        { initiativeId: string; scope?: string; activityType?: string; periodStart?: string; periodEnd?: string; includeChildren?: boolean }
      >({
        queryFn: async ({ initiativeId, ...body }, _api, _extraOptions, baseQuery) => {
          const result = await baseQuery({
            url: `/initiatives/${initiativeId}/emissions/activities/csv-export`,
            method: 'post',
            data: body,
            responseType: 'blob',
          });

          if (result.error) {
            return { error: result.error };
          }

          const blob = result.data as Blob;
          const meta = result.meta as AxiosQueryMeta | undefined;
          const contentDisposition = meta?.headers?.['content-disposition'] ?? '';
          const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
          const filename = filenameMatch?.[1] ?? `activities-export-${new Date().toISOString().slice(0, 10)}.csv`;

          return { data: { blob, filename } };
        },
      }),

      // Facilities Comparison - compare emissions across child initiatives
      getFacilitiesComparison: builder.query<FacilitiesComparisonResponse, FacilitiesComparisonParams>({
        transformResponse,
        query: ({ initiativeId, periodStart, periodEnd, groupBy, includeChildren }) => ({
          url: `/initiatives/${initiativeId}/emissions/reports/facilities-comparison`,
          method: 'post',
          data: { periodStart, periodEnd, groupBy, includeChildren },
        }),
        providesTags: [FACILITIES_COMPARISON_TAG],
      }),

      // Monthly by Entity - CO2e per month per entity (initiative or asset) for trend lines
      getMonthlyByEntity: builder.query<EntityMonthlyResponse, { initiativeId: string; periodStart: string; periodEnd: string; groupBy: 'initiative' | 'asset' }>({
        transformResponse,
        query: ({ initiativeId, ...data }) => ({
          url: `/initiatives/${initiativeId}/emissions/reports/monthly-by-entity`,
          method: 'post',
          data,
        }),
        providesTags: [REPORTS_TAG, FACILITIES_COMPARISON_TAG],
      }),

      // Intensity Treemap - CO2e + business metrics per child initiative
      getIntensityTreemap: builder.query<IntensityTreemapResponse, { initiativeId: string; periodStart: string; periodEnd: string }>({
        transformResponse,
        query: ({ initiativeId, periodStart, periodEnd }) => ({
          url: `/initiatives/${initiativeId}/emissions/reports/intensity-treemap`,
          method: 'post',
          data: { periodStart, periodEnd },
        }),
        providesTags: [FACILITIES_COMPARISON_TAG, METRICS_TAG],
      }),

      // Settings - stored at root initiative only
      getSettings: builder.query<EmissionSettings, { initiativeId: string }>({
        transformResponse,
        query: ({ initiativeId }) => ({
          url: `/initiatives/${initiativeId}/emissions/settings`,
          method: 'get',
        }),
        providesTags: [SETTINGS_TAG],
      }),

      updateAllSettings: builder.mutation<
        EmissionSettings,
        { initiativeId: string; data: UpdateAllSettingsRequest }
      >({
        transformResponse,
        invalidatesTags: [SETTINGS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, data }) => ({
          url: `/initiatives/${initiativeId}/emissions/settings`,
          method: 'put',
          data,
        }),
      }),

      // Onboarding — single aggregated status for the dashboard first-run UX
      getOnboardingStatus: builder.query<OnboardingStatusResponse, { initiativeId: string }>({
        transformResponse,
        query: ({ initiativeId }) => ({
          url: `/initiatives/${initiativeId}/emissions/settings/onboarding-status`,
          method: 'get',
        }),
        providesTags: [ONBOARDING_TAG],
      }),

      updateOnboardingStatus: builder.mutation<
        { status: OnboardingStatus },
        { initiativeId: string; status: OnboardingStatus }
      >({
        transformResponse,
        invalidatesTags: [ONBOARDING_TAG],
        query: ({ initiativeId, status }) => ({
          url: `/initiatives/${initiativeId}/emissions/settings/onboarding-status`,
          method: 'patch',
          data: { status },
        }),
      }),

      // --- Emissions PPTX Report ---

      generateEmissionsPPTXReport: builder.mutation<
        { jobId: string; status: string },
        { initiativeId: string; periodStart: string; periodEnd: string }
      >({
        invalidatesTags: [GENERATED_REPORTS_TAG],
        query: ({ initiativeId, periodStart, periodEnd }) => ({
          url: `/initiatives/${initiativeId}/emissions/report/pptx`,
          method: 'post',
          data: { periodStart, periodEnd },
        }),
      }),

      getEmissionsPPTXReports: builder.query<EmissionsPPTXReportItem[], { initiativeId: string }>({
        transformResponse,
        query: ({ initiativeId }) => ({
          url: `/initiatives/${initiativeId}/emissions/reports/pptx`,
          method: 'get',
        }),
        providesTags: [GENERATED_REPORTS_TAG],
      }),

      getEmissionsPPTXReportDownloadUrl: builder.query<
        { downloadUrl: string },
        { initiativeId: string; jobId: string; taskId: string }
      >({
        transformResponse,
        query: ({ initiativeId, jobId, taskId }) => ({
          url: `/initiatives/${initiativeId}/emissions/report/${jobId}/${taskId}/download`,
          method: 'get',
        }),
      }),

      deleteEmissionsPPTXReport: builder.mutation<
        void,
        { initiativeId: string; jobId: string; taskId: string }
      >({
        invalidatesTags: [GENERATED_REPORTS_TAG],
        query: ({ initiativeId, jobId, taskId }) => ({
          url: `/initiatives/${initiativeId}/emissions/report/${jobId}/${taskId}`,
          method: 'delete',
        }),
      }),

      // Bulk Imports
      // `file` is optional: in-memory groups (manually-staged rows from the
      // multi-add Entry step) post without an XLSX. The server records a
      // BulkImport with no fileId in that case.
      createBulkImport: builder.mutation<
        BulkImport,
        {
          initiativeId: string;
          file?: File;
          activities: string;
          scope: string;
          activityType?: string;
          regionFallback: boolean;
          yearFallback: boolean;
        }
      >({
        transformResponse,
        invalidatesTags: [ACTIVITIES_TAG, CALCULATIONS_TAG, IMPORTS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, file, activities, scope, activityType, regionFallback, yearFallback }) => {
          const formData = new FormData();
          if (file) {
            formData.append('file', file);
          }
          formData.append('activities', activities);
          formData.append('scope', scope);
          if (activityType) {
            formData.append('activityType', activityType);
          }
          formData.append('regionFallback', String(regionFallback));
          formData.append('yearFallback', String(yearFallback));
          return {
            url: `/initiatives/${initiativeId}/emissions/imports`,
            method: 'post',
            data: formData,
          };
        },
      }),

      /**
       * Scan Invoice (GU-6981). Triggers OCR on a single file already staged
       * in an upload session. Body is `{ sessionId, fileId }` — the file
       * bytes stay in the temp bucket and ai-service fetches via a signed
       * URL emitted by the emissions service. Preview-only — no DB writes.
       */
      extractInvoice: builder.mutation<
        ExtractInvoiceResult,
        { initiativeId: string; sessionId: string; fileId: string }
      >({
        transformResponse,
        query: ({ initiativeId, sessionId, fileId }) => ({
          url: `/initiatives/${initiativeId}/emissions/imports/extract-invoice`,
          method: 'post',
          data: { sessionId, fileId },
        }),
      }),

      getImports: builder.query<
        { data: BulkImport[]; pagination: { total: number; limit: number; offset: number } },
        { initiativeId: string; limit?: number; offset?: number }
      >({
        transformResponse: (response: PaginatedApiResponse<BulkImport>) => ({
          data: response.data,
          pagination: response.pagination,
        }),
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/imports`,
          method: 'get',
          params,
        }),
        providesTags: [IMPORTS_TAG],
      }),

      getImport: builder.query<BulkImport, { initiativeId: string; importId: string }>({
        transformResponse,
        query: ({ initiativeId, importId }) => ({
          url: `/initiatives/${initiativeId}/emissions/imports/${importId}`,
          method: 'get',
        }),
        providesTags: [IMPORTS_TAG],
      }),

      getImportDownloadUrl: builder.query<{ downloadUrl: string }, { initiativeId: string; importId: string }>({
        transformResponse,
        query: ({ initiativeId, importId }) => ({
          url: `/initiatives/${initiativeId}/emissions/imports/${importId}/download`,
          method: 'get',
        }),
      }),

      undoImport: builder.mutation<{ deletedCount: number }, { initiativeId: string; importId: string }>({
        transformResponse,
        invalidatesTags: [ACTIVITIES_TAG, CALCULATIONS_TAG, IMPORTS_TAG, ASSETS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, importId }) => ({
          url: `/initiatives/${initiativeId}/emissions/imports/${importId}/undo`,
          method: 'post',
        }),
      }),

      // ====================================================================
      // Assets CRUD
      // ====================================================================

      getAssets: builder.query<AssetListResponse, { initiativeId: string; assetType?: AssetType; search?: string; limit?: number; offset?: number; sort?: string; sortDir?: 'asc' | 'desc'; includeChildren?: boolean }>({
        transformResponse: (response: PaginatedApiResponse<EmissionAsset>) => ({
          data: response.data as unknown as EmissionAsset[],
          pagination: response.pagination,
        }),
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/assets`,
          method: 'get',
          params,
        }),
        providesTags: [ASSETS_TAG],
      }),

      getAsset: builder.query<EmissionAsset, { initiativeId: string; assetId: string }>({
        transformResponse: (response: ApiResponse<EmissionAsset>) => response.data,
        query: ({ initiativeId, assetId }) => ({
          url: `/initiatives/${initiativeId}/emissions/assets/${assetId}`,
          method: 'get',
        }),
        providesTags: [ASSETS_TAG],
      }),

      createAsset: builder.mutation<EmissionAsset, { initiativeId: string; data: CreateAssetRequest }>({
        transformResponse,
        invalidatesTags: [ASSETS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, data }) => ({
          url: `/initiatives/${initiativeId}/emissions/assets`,
          method: 'post',
          data,
        }),
      }),

      updateAsset: builder.mutation<EmissionAsset, { initiativeId: string; assetId: string; data: UpdateAssetRequest }>({
        transformResponse,
        invalidatesTags: [ASSETS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, assetId, data }) => ({
          url: `/initiatives/${initiativeId}/emissions/assets/${assetId}`,
          method: 'put',
          data,
        }),
      }),

      deleteAsset: builder.mutation<void, { initiativeId: string; assetId: string; mode: AssetDeleteMode }>({
        invalidatesTags: [ASSETS_TAG, ACTIVITIES_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, assetId, mode }) => ({
          url: `/initiatives/${initiativeId}/emissions/assets/${assetId}`,
          method: 'delete',
          data: { mode },
        }),
      }),

      assignActivitiesToAsset: builder.mutation<{ updated: number }, { initiativeId: string; assetId: string; activityIds: string[] }>({
        transformResponse,
        invalidatesTags: [ACTIVITIES_TAG, ASSETS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, assetId, activityIds }) => ({
          url: `/initiatives/${initiativeId}/emissions/assets/${assetId}/assign`,
          method: 'post',
          data: { activityIds },
        }),
      }),

      unassignActivitiesFromAsset: builder.mutation<{ updated: number }, { initiativeId: string; activityIds: string[] }>({
        transformResponse,
        invalidatesTags: [ACTIVITIES_TAG, ASSETS_TAG, ONBOARDING_TAG],
        query: ({ initiativeId, activityIds }) => ({
          url: `/initiatives/${initiativeId}/emissions/assets/unassign`,
          method: 'post',
          data: { activityIds },
        }),
      }),

      // Asset reporting
      getAssetSummary: builder.query<AssetSummary, { initiativeId: string; assetId: string; periodStart?: string; periodEnd?: string }>({
        transformResponse,
        query: ({ initiativeId, assetId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/assets/${assetId}/summary`,
          method: 'get',
          params,
        }),
        providesTags: [ASSETS_TAG, REPORTS_TAG],
      }),

      getAssetComparison: builder.query<AssetComparisonResponse, { initiativeId: string; assetIds?: string[]; periodStart: string; periodEnd: string; includeChildren?: boolean }>({
        transformResponse,
        query: ({ initiativeId, ...data }) => ({
          url: `/initiatives/${initiativeId}/emissions/assets/comparison`,
          method: 'post',
          data,
        }),
        providesTags: [ASSETS_TAG, REPORTS_TAG],
      }),

      getAssetByMonth: builder.query<MonthlyReport[], { initiativeId: string; assetId: string; periodStart: string; periodEnd: string }>({
        transformResponse: (response: ApiResponse<{ results: MonthlyReport[] }>) => response.data.results,
        query: ({ initiativeId, assetId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/assets/${assetId}/by-month`,
          method: 'get',
          params,
        }),
        providesTags: [ASSETS_TAG, REPORTS_TAG],
      }),

      // ======================================================================
      // Evidence — direct upload + listing + signed download + attach/detach.
      // Batch upload uses the session endpoints below.
      // ======================================================================

      listEmissionEvidence: builder.query<
        { items: EmissionEvidenceListItem[]; total: number; limit: number; offset: number },
        {
          initiativeId: string;
          activityId?: string;
          evidenceType?: EvidenceType;
          issuer?: string;
          /** Case-insensitive substring match across filename + issuer, applied server-side. */
          search?: string;
          limit?: number;
          offset?: number;
        }
      >({
        transformResponse: (response: PaginatedApiResponse<EmissionEvidenceListItem>) => ({
          items: response.data,
          total: response.pagination.total,
          limit: response.pagination.limit,
          offset: response.pagination.offset,
        }),
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/evidence`,
          method: 'get',
          params,
        }),
        providesTags: [EVIDENCE_TAG],
      }),

      uploadEmissionEvidence: builder.mutation<
        UploadEvidenceResult,
        {
          initiativeId: string;
          file: File;
          evidenceType: EvidenceType;
          description?: string;
          documentDate?: string;
          periodStart?: string;
          periodEnd?: string;
          issuer?: string;
          tags?: string[];
          activityId?: string;
        }
      >({
        transformResponse,
        invalidatesTags: [EVIDENCE_TAG, ACTIVITIES_TAG, CALCULATIONS_TAG],
        query: ({ initiativeId, file, tags, ...metadata }) => {
          const formData = new FormData();
          formData.append('file', file);
          Object.entries(metadata).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              formData.append(key, String(value));
            }
          });
          if (tags && tags.length > 0) {
            formData.append('tags', JSON.stringify(tags));
          }
          return {
            url: `/initiatives/${initiativeId}/emissions/evidence`,
            method: 'post',
            data: formData,
          };
        },
      }),

      deleteEmissionEvidence: builder.mutation<void, { initiativeId: string; evidenceId: string }>({
        invalidatesTags: [EVIDENCE_TAG, ACTIVITIES_TAG, CALCULATIONS_TAG],
        query: ({ initiativeId, evidenceId }) => ({
          url: `/initiatives/${initiativeId}/emissions/evidence/${evidenceId}`,
          method: 'delete',
        }),
      }),

      getEmissionEvidenceDownloadUrl: builder.query<
        { url: string } | null,
        { initiativeId: string; evidenceId: string }
      >({
        transformResponse: (response: ApiResponse<{ url: string } | null>) => response.data,
        query: ({ initiativeId, evidenceId }) => ({
          url: `/initiatives/${initiativeId}/emissions/evidence/${evidenceId}/download`,
          method: 'get',
        }),
      }),

      getEvidenceLinkedActivities: builder.query<
        EvidenceActivityLink[],
        { initiativeId: string; evidenceId: string }
      >({
        transformResponse,
        providesTags: [ACTIVITIES_TAG, EVIDENCE_TAG],
        query: ({ initiativeId, evidenceId }) => ({
          url: `/initiatives/${initiativeId}/emissions/evidence/${evidenceId}/activities`,
          method: 'get',
        }),
      }),

      attachEvidenceToActivity: builder.mutation<
        { attached: boolean },
        { initiativeId: string; evidenceId: string; activityId: string; description?: string }
      >({
        transformResponse,
        invalidatesTags: [EVIDENCE_TAG, ACTIVITIES_TAG, CALCULATIONS_TAG],
        query: ({ initiativeId, evidenceId, ...data }) => ({
          url: `/initiatives/${initiativeId}/emissions/evidence/${evidenceId}/attach`,
          method: 'post',
          data,
        }),
      }),

      detachEvidenceFromActivity: builder.mutation<
        { detached: boolean },
        { initiativeId: string; evidenceId: string; activityId: string }
      >({
        transformResponse,
        invalidatesTags: [EVIDENCE_TAG, ACTIVITIES_TAG, CALCULATIONS_TAG],
        query: ({ initiativeId, evidenceId, activityId }) => ({
          url: `/initiatives/${initiativeId}/emissions/evidence/${evidenceId}/attach/${activityId}`,
          method: 'delete',
        }),
      }),

      // ======================================================================
      // Upload sessions — batch staging for evidence during data entry.
      // ======================================================================

      createUploadSession: builder.mutation<UploadSession, { initiativeId: string }>({
        transformResponse,
        invalidatesTags: [SESSION_TAG],
        query: ({ initiativeId }) => ({
          url: `/initiatives/${initiativeId}/emissions/sessions`,
          method: 'post',
        }),
      }),

      getUploadSession: builder.query<
        UploadSession | null,
        { initiativeId: string; sessionId: string }
      >({
        transformResponse: (response: ApiResponse<UploadSession | null>) => response.data,
        query: ({ initiativeId, sessionId }) => ({
          url: `/initiatives/${initiativeId}/emissions/sessions/${sessionId}`,
          method: 'get',
        }),
        providesTags: (_result, _err, { sessionId }) => [{ type: SESSION_TAG, id: sessionId }],
      }),

      listUploadSessions: builder.query<
        UploadSession[],
        { initiativeId: string; status?: UploadSessionStatus }
      >({
        transformResponse,
        query: ({ initiativeId, ...params }) => ({
          url: `/initiatives/${initiativeId}/emissions/sessions`,
          method: 'get',
          params,
        }),
        providesTags: [SESSION_TAG],
      }),

      uploadToSession: builder.mutation<
        SessionFile,
        {
          initiativeId: string;
          sessionId: string;
          file: File;
          /** Optional seed metadata — e.g. a filename-keyword evidenceType
           * guess from the drop handler — so a drop batch is one POST per
           * file instead of POST + PATCH. */
          metadata?: SessionFileMetadataUpdate;
        }
      >({
        transformResponse,
        invalidatesTags: (_result, _err, { sessionId }) => [
          { type: SESSION_TAG, id: sessionId },
          SESSION_TAG,
        ],
        query: ({ initiativeId, sessionId, file, metadata }) => {
          const formData = new FormData();
          formData.append('file', file);
          if (metadata) {
            for (const [key, value] of Object.entries(metadata)) {
              if (value === undefined || value === null) {
                continue;
              }
              formData.append(key, Array.isArray(value) ? value.join(',') : String(value));
            }
          }
          return {
            url: `/initiatives/${initiativeId}/emissions/sessions/${sessionId}/files`,
            method: 'post',
            data: formData,
          };
        },
      }),

      updateSessionFileMetadata: builder.mutation<
        SessionFile,
        { initiativeId: string; sessionId: string; fileId: string; metadata: SessionFileMetadataUpdate }
      >({
        transformResponse,
        invalidatesTags: (_result, _err, { sessionId }) => [
          { type: SESSION_TAG, id: sessionId },
          SESSION_TAG,
        ],
        query: ({ initiativeId, sessionId, fileId, metadata }) => ({
          url: `/initiatives/${initiativeId}/emissions/sessions/${sessionId}/files/${fileId}`,
          method: 'patch',
          data: metadata,
        }),
      }),

      removeFromSession: builder.mutation<
        { removed: boolean },
        { initiativeId: string; sessionId: string; fileId: string }
      >({
        transformResponse,
        invalidatesTags: (_result, _err, { sessionId }) => [
          { type: SESSION_TAG, id: sessionId },
          SESSION_TAG,
        ],
        query: ({ initiativeId, sessionId, fileId }) => ({
          url: `/initiatives/${initiativeId}/emissions/sessions/${sessionId}/files/${fileId}`,
          method: 'delete',
        }),
      }),

      promoteSessionFile: builder.mutation<
        PromoteFileResult,
        {
          initiativeId: string;
          sessionId: string;
          fileId: string;
          activityId?: string;
          metadata?: SessionFileMetadataUpdate;
        }
      >({
        transformResponse,
        invalidatesTags: (_result, _err, { sessionId }) => [
          { type: SESSION_TAG, id: sessionId },
          SESSION_TAG,
          EVIDENCE_TAG,
          ACTIVITIES_TAG,
          CALCULATIONS_TAG,
        ],
        query: ({ initiativeId, sessionId, fileId, ...data }) => ({
          url: `/initiatives/${initiativeId}/emissions/sessions/${sessionId}/files/${fileId}/promote`,
          method: 'post',
          data,
        }),
      }),

      commitUploadSession: builder.mutation<
        { promoted: number; abandoned: number },
        { initiativeId: string; sessionId: string }
      >({
        transformResponse,
        invalidatesTags: (_result, _err, { sessionId }) => [
          { type: SESSION_TAG, id: sessionId },
          SESSION_TAG,
        ],
        query: ({ initiativeId, sessionId }) => ({
          url: `/initiatives/${initiativeId}/emissions/sessions/${sessionId}/commit`,
          method: 'post',
        }),
      }),

      discardUploadSession: builder.mutation<
        { discarded: boolean },
        { initiativeId: string; sessionId: string }
      >({
        transformResponse,
        invalidatesTags: (_result, _err, { sessionId }) => [
          { type: SESSION_TAG, id: sessionId },
          SESSION_TAG,
        ],
        query: ({ initiativeId, sessionId }) => ({
          url: `/initiatives/${initiativeId}/emissions/sessions/${sessionId}`,
          method: 'delete',
        }),
      }),
    }),
  });

export const {
  useGetDataQualitySummaryQuery,
  useGetActivityYearsQuery,
  useGetActivitiesQuery,
  useGetActivityQuery,
  useCreateActivityMutation,
  useUpdateActivityMutation,
  useDeleteActivityMutation,
  useBulkDeleteActivitiesMutation,
  useBulkCreateActivitiesMutation,
  useGetCalculationsQuery,
  useGetCalculationQuery,
  useTriggerCalculationMutation,
  useCalculateScope2DualMutation,
  useRecalculateAllMutation,
  useGetCalculationHistoryQuery,
  useGetEmissionsSummaryQuery,
  useGetEmissionsByActivityTypeQuery,
  useGetEmissionsByMonthQuery,
  useGetEmissionsBySourcePeriodQuery,
  useGetScope2DualSummaryQuery,
  useLazyExportEmissionsQuery,
  useSeedSampleDataMutation,
  useGetIntensityMetricsQuery,
  useGetBusinessMetricsQuery,
  useGetBusinessMetricsListQuery,
  useUpdateBusinessMetricsMutation,
  useDeleteBusinessMetricsMutation,
  useGetTargetsQuery,
  useGetTargetQuery,
  useCreateTargetMutation,
  useUpdateTargetMutation,
  useDeleteTargetMutation,
  useGetEmissionFactorsQuery,
  useGetActivityOptionsQuery,
  useLazyGetActivityImportTemplateQuery,
  useLazyGetActivitySeedTemplateQuery,
  useLazyGetFactorImportTemplateQuery,
  useImportFactorsFromExcelMutation,
  useDeleteEmissionFactorMutation,
  useExportActivitiesMutation,
  useExportActivitiesCSVMutation,
  useGetFacilitiesComparisonQuery,
  useGetMonthlyByEntityQuery,
  useGetIntensityTreemapQuery,
  useGetSettingsQuery,
  useUpdateAllSettingsMutation,
  useGetOnboardingStatusQuery,
  useUpdateOnboardingStatusMutation,
  useGenerateEmissionsPPTXReportMutation,
  useGetEmissionsPPTXReportsQuery,
  useLazyGetEmissionsPPTXReportDownloadUrlQuery,
  useDeleteEmissionsPPTXReportMutation,
  useCreateBulkImportMutation,
  useExtractInvoiceMutation,
  useGetImportsQuery,
  useGetImportQuery,
  useLazyGetImportDownloadUrlQuery,
  useUndoImportMutation,
  useGetFactorAlternativesQuery,
  useFindFactorAlternativesMutation,
  useFactorSearchMutation,
  useFactorSearchPaginatedMutation,
  useOverrideCalculationFactorMutation,
  useRevertCalculationVersionMutation,
  // Asset Bulk Import
  useLazyGetAssetImportTemplateQuery,
  useLazyGetAssetSeedTemplateQuery,
  useCreateBulkAssetImportMutation,
  // Assets
  useGetAssetsQuery,
  useGetAssetQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useDeleteAssetMutation,
  useAssignActivitiesToAssetMutation,
  useUnassignActivitiesFromAssetMutation,
  useGetAssetSummaryQuery,
  useGetAssetComparisonQuery,
  useGetAssetByMonthQuery,
  // Evidence
  useListEmissionEvidenceQuery,
  useUploadEmissionEvidenceMutation,
  useDeleteEmissionEvidenceMutation,
  useLazyGetEmissionEvidenceDownloadUrlQuery,
  useGetEvidenceLinkedActivitiesQuery,
  useAttachEvidenceToActivityMutation,
  useDetachEvidenceFromActivityMutation,
  // Upload sessions
  useCreateUploadSessionMutation,
  useGetUploadSessionQuery,
  useListUploadSessionsQuery,
  useUploadToSessionMutation,
  useUpdateSessionFileMetadataMutation,
  useRemoveFromSessionMutation,
  usePromoteSessionFileMutation,
  useCommitUploadSessionMutation,
  useDiscardUploadSessionMutation,
} = emissionsApi;
